/**
 * AssemblerClient - Core client-side merge logic for all Node engines
 * Port of csharp/AssemblerQuality/ConfigClient/AssemblerClient.cs
 *
 * Uses ConfigClient (IConfig) with API JSON response data to merge templates
 * client-side via the 4 supported engines: Normal, PreProcess, NormalJson,
 * PreProcessJson. AssemblerFlex (Flex/Flat) exists only in C#.
 *
 * Additional Node-only helpers (fetchTemplates, getComponentsServer) support
 * the browser bundles and have no C# equivalent — they live here because the
 * browser bundles re-export from this file.
 */

import { EngineNormal, EnginePreProcess, LoaderNormal, LoaderPreProcess } from '@arshu/assembler';
import type { ILoaderNormal, ILoaderPreProcess, MergeResult, OperationsMetadata } from '@arshu/assembler';
import { JsonObject, JsonUtility } from '@arshu/assembler/app';
import { ConfigClient } from '@arshu/assemblerjson/config';
import { EngineNormalJson, EnginePreProcessJson, LoaderNormalJson, LoaderPreProcessJson } from '@arshu/assemblerjson';
import type { ILoaderNormalJson, ILoaderPreProcessJson } from '@arshu/assemblerjson';

type PlainJsonObject = { [key: string]: unknown };
type PlainJsonMap = globalThis.Map <string, unknown>;

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body: string;
}

interface FetchResponse {
    ok: boolean;
    status: number;
    text(): Promise<string>;
    json(): Promise<unknown>;
}

type FetchFn = (url: string, options: FetchOptions) => Promise<FetchResponse>;

interface ApiTemplatesResponse {
    ContextJson?: PlainJsonObject;
    contextJson?: PlainJsonObject;
    RawData?: Record<string, Record<string, JsonObject>>;
    rawData?: Record<string, Record<string, JsonObject>>;
    [key: string]: unknown;
}

interface ComponentsServerResponse {
    ComponentHtml?: string;
    ComponentId?: string;
    [key: string]: unknown;
}

export interface ClientMergeResult {
    html: string;
    appFile: string;
    operations: OperationsMetadata | null;
    componentHtml: JsonObject;
    dynamicView: JsonObject;
    dynamicData: JsonObject;
    dynamicAction: JsonObject;
}

export class AssemblerClient {
    static async fetchTemplates(
        fetchFn: FetchFn,
        appSite: string,
        appView: string,
        engineType: string,
        mergeMode: 'ClientJs' | 'ClientWasm'
    ): Promise<ApiTemplatesResponse> {
        const response = await fetchFn('/api/get-templates', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                appSite: appSite,
                appView: appView || '',
                engineType: engineType,
                mergeMode: mergeMode
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error('HTTP error! status: ' + response.status + ' - ' + errorText);
        }

        return await response.json() as ApiTemplatesResponse;
    }

    static async getComponentsServer(
        fetchFn: FetchFn,
        appSite: string,
        appView: string,
        engineType: string,
        componentName: string,
        jsonConfig: JsonObject | null = null,
        publishMode: string | null = null
    ): Promise<ComponentsServerResponse> {
        const requestBody = new JsonObject();
        requestBody.set('appSite', appSite);
        requestBody.set('appView', appView || '');
        requestBody.set('engineType', engineType);
        requestBody.set('refreshComponent', componentName);
        requestBody.set('jsonConfig', jsonConfig || new JsonObject());
        // Connected => the server broadcasts the merged component to other connected clients. Only send
        // the field when non-default so a plain Self merge keeps the previous request shape (mirrors
        // performServerGroupRefresh, the Loader's Connected path).
        if (publishMode && publishMode !== 'Self') {
            requestBody.set('publishMode', publishMode);
        }
        const response = await fetchFn('/api/get-components', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JsonUtility.serializeObject(requestBody)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error('HTTP error! status: ' + response.status + ' - ' + errorText);
        }

        return await response.json() as ComponentsServerResponse;
    }

    static getComponentsClient(
        apiResponse: ApiTemplatesResponse,
        appSite: string,
        appView: string,
        engineType: string,
        componentName: string,
        configJson: JsonObject | null = null,
        deliveredClientData: JsonObject | null = null
    ): ClientMergeResult {
        const baseContextJson = apiResponse.ContextJson || apiResponse.contextJson || {};
        const contextJson = AssemblerClient.prepareContextJson(
            baseContextJson,
            appSite,
            appView || '',
            engineType
        );

        const actionMode = AssemblerClient.extractActionMode(configJson);
        const clientConfig = new ConfigClient(JsonObject.fromPlainObject(apiResponse as unknown as Parameters<typeof JsonObject.fromPlainObject>[0]));
        // Route the server's delivered client-data islands (MergeResult.dynamicClientData) into the client
        // config so the clientMode re-merge below prepends each component's island into the delivered HTML and
        // the Client Handler consumes it — reproducing the server output for client-data scenarios.
        clientConfig.dynamicClientData = deliveredClientData;

        let result: MergeResult;
        if (engineType === 'PreProcessJson') {
            const loader = new LoaderPreProcessJson(clientConfig, appSite) as unknown as ILoaderPreProcessJson;
            const engine = new EnginePreProcessJson(loader);
            result = engine.mergeTemplates(appSite, appView || '', true, true, contextJson, configJson, actionMode, true);
        } else if (engineType === 'NormalJson') {
            const loader = new LoaderNormalJson(clientConfig, appSite) as unknown as ILoaderNormalJson;
            const engine = new EngineNormalJson(loader);
            result = engine.mergeTemplates(appSite, appView || '', true, true, contextJson, configJson, actionMode, true);
        } else if (engineType === 'PreProcess') {
            const loader = new LoaderPreProcess(clientConfig, appSite) as unknown as ILoaderPreProcess;
            const engine = new EnginePreProcess(loader);
            result = engine.mergeTemplates(appSite, appView || '', true, true, contextJson, configJson, actionMode, true);
        } else {
            const loader = new LoaderNormal(clientConfig, appSite) as unknown as ILoaderNormal;
            const engine = new EngineNormal(loader);
            result = engine.mergeTemplates(appSite, appView || '', true, true, contextJson, configJson, actionMode, true);
        }

        let html = '';
        if (result.componentHtml.has(componentName)) {
            html = result.componentHtml.get(componentName).asString() || '';
        }

        return {
            html: html,
            appFile: result.appFile || '',
            operations: result.operations || null,
            componentHtml: result.componentHtml,
            dynamicView: result.dynamicView || new JsonObject(),
            dynamicData: result.dynamicData || new JsonObject(),
            dynamicAction: result.dynamicAction || new JsonObject()
        };
    }

    static prepareContextJson(
        baseContextJson: PlainJsonObject | PlainJsonMap,
        appSite: string,
        appView: string,
        engineType: string
    ): JsonObject {
        const contextJson = new JsonObject();

        if (baseContextJson) {
            if (baseContextJson instanceof Map) {
                for (const [key, value] of baseContextJson) {
                    contextJson.set(key as string, value);
                }
            } else {
                for (const key in baseContextJson) {
                    if (Object.prototype.hasOwnProperty.call(baseContextJson, key)) {
                        contextJson.set(key, baseContextJson[key]);
                    }
                }
            }
        }

        contextJson.set('AppSite', appSite);
        contextJson.set('AppView', appView || '');
        contextJson.set('EngineType', engineType);
        return contextJson;
    }

    static extractActionMode(configJson: JsonObject | null): boolean {
        if (!configJson) {
            return false;
        }

        if (configJson.has('ActionMode')) {
            const actionModeValue = configJson.get('ActionMode');
            if (actionModeValue && actionModeValue.isBool) {
                return actionModeValue.getBool();
            } else if (actionModeValue && actionModeValue.isString && actionModeValue.asString() === 'true') {
                return true;
            }
        }

        return false;
    }

}
